import React, { useRef } from 'react';
import { useAct, gsap, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   CANLI PANEL
   Uygulamanın gerçek gösterge ekranı, canlı animasyonlu.
   Kaydırma bölüme girince barlar büyüyor, trend çizgisi
   çiziliyor, dairesel göstergeler dolup sayılar sayılıyor.
   Uç noktalar hafifçe nabız gibi parlıyor (canlı hissi).
   ══════════════════════════════════════════════════════ */

const NDVI = [92, 79, 67, 56, 42, 34, 27, 21, 17, 11];
const RAIN = [92, 115, 170, 145, 133, 147, 128, 57, 113, 52, 116, 128];
const TREND = 'M14,26 C70,20 96,120 150,118 C196,116 214,128 286,140';

const leaf = (
  <path d="M5 21c8 0 14-6 14-14V4h-3C8 4 3 10 3 18v3h2Zm0 0c0-6 3-10 9-13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
);
const trend = (
  <>
    <path d="M3 17l5-6 4 4 8-9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M15 6h6v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </>
);
const drop = (
  <path d="M12 3s6 6.5 6 11a6 6 0 1 1-12 0c0-4.5 6-11 6-11Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none" />
);
const tractor = (
  <>
    <circle cx="7" cy="17" r="3.2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <circle cx="18" cy="18" r="2.2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M4 14V8h5l2 5h5v3M9 8V5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </>
);

function Icon({ children, tint }) {
  return (
    <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
      style={{ background: `${tint}1f`, color: tint }}>
      <svg width="20" height="20" viewBox="0 0 24 24">{children}</svg>
    </span>
  );
}

function Badge({ text, tint, up }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[12px] font-medium"
      style={{ background: `${tint}1c`, color: tint }}>
      {text}{up && <span aria-hidden="true">↑</span>}
    </span>
  );
}

function Card({ icon, tint, title, sub, badge, children }) {
  return (
    <div className="dash-card relative rounded-[20px] border p-5 md:p-6 overflow-hidden"
      style={{ background: 'linear-gradient(160deg,#0d141b 0%,#0a1015 100%)', borderColor: 'rgba(255,255,255,.08)' }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Icon tint={tint}>{icon}</Icon>
          <div className="min-w-0">
            <div className="font-display font-semibold text-white text-[16px] leading-tight truncate">{title}</div>
            <div className="text-[12px] text-white/45 mt-0.5">{sub}</div>
          </div>
        </div>
        {badge}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

export default function LiveDashboard() {
  const num1 = useRef(null);
  const num2 = useRef(null);
  const num3 = useRef(null);

  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    const bars = q('.d-bar');
    const rings = q('.d-ring-fill');
    const line = q('.d-line')[0];

    const setNum = (el, v, suffix) => { if (el) el.textContent = Math.round(v).toLocaleString('tr-TR') + (suffix || ''); };

    if (prefersReduced()) {
      gsap.set(bars, { scaleY: 1 });
      rings.forEach((r) => gsap.set(r, { strokeDashoffset: r.dataset.target }));
      if (line) gsap.set(line, { strokeDashoffset: 0 });
      setNum(num1.current, 1707); setNum(num2.current, 82, '%'); setNum(num3.current, 94, '%');
      return;
    }

    gsap.set(bars, { scaleY: 0, transformOrigin: '50% 100%' });
    rings.forEach((r) => { const C = r.dataset.c; gsap.set(r, { strokeDasharray: C, strokeDashoffset: C }); });
    if (line) { const L = line.getTotalLength(); gsap.set(line, { strokeDasharray: L, strokeDashoffset: L }); }

    const cards = q('.dash-card');
    gsap.from(cards, {
      opacity: 0, y: 36, ease: 'power3.out', duration: 0.7, stagger: 0.1,
      scrollTrigger: { trigger: root, start: 'top 72%' },
    });

    const tl = gsap.timeline({ scrollTrigger: { trigger: root, start: 'top 62%' } });

    tl.to(bars, { scaleY: 1, ease: 'power2.out', duration: 0.8, stagger: 0.03 }, 0.1);
    if (line) tl.to(line, { strokeDashoffset: 0, ease: 'power2.inOut', duration: 1.2 }, 0.2);

    rings.forEach((r, i) => {
      tl.to(r, { strokeDashoffset: r.dataset.target, ease: 'power2.out', duration: 1.3 }, 0.3 + i * 0.12);
    });

    const c1 = { v: 0 }, c2 = { v: 0 }, c3 = { v: 0 };
    tl.to(c1, { v: 1707, duration: 1.3, ease: 'power2.out', onUpdate: () => setNum(num1.current, c1.v) }, 0.3);
    tl.to(c2, { v: 82, duration: 1.3, ease: 'power2.out', onUpdate: () => setNum(num2.current, c2.v, '%') }, 0.42);
    tl.to(c3, { v: 94, duration: 1.3, ease: 'power2.out', onUpdate: () => setNum(num3.current, c3.v, '%') }, 0.54);

    /* canlı nabız: uç noktalar sürekli hafif parlıyor */
    gsap.to(q('.d-dot'), { scale: 1.25, opacity: 0.65, duration: 1.1, ease: 'sine.inOut', yoyo: true, repeat: -1, stagger: 0.2, transformOrigin: '50% 50%' });
  }, []);

  /* dairesel gösterge */
  const R = 52, C = 2 * Math.PI * R;
  const Gauge = ({ pct, from, to, numRef, label, sub }) => {
    const target = C * (1 - pct / 100);
    const id = `g${Math.round(pct)}${from.slice(1)}`;
    return (
      <div className="relative mx-auto" style={{ width: 168, height: 168 }}>
        <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={from} /><stop offset="100%" stopColor={to} />
            </linearGradient>
          </defs>
          <circle cx="70" cy="70" r={R} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="11" />
          <circle className="d-ring-fill" cx="70" cy="70" r={R} fill="none" stroke={`url(#${id})`} strokeWidth="11"
            strokeLinecap="round" data-c={C} data-target={target} style={{ strokeDasharray: C, strokeDashoffset: C }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span ref={numRef} className="font-display font-bold text-white tabular-nums leading-none" style={{ fontSize: 'clamp(30px,4vw,44px)' }}>0{sub}</span>
          <span className="mt-1.5 text-[12.5px] text-white/50">{label}</span>
        </div>
      </div>
    );
  };

  return (
    <section ref={ref} id="panel" className="relative overflow-hidden" style={{ background: '#050608' }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: 'radial-gradient(ellipse 60% 45% at 50% 10%, rgba(56,189,248,.07) 0%, rgba(56,189,248,0) 60%)' }} />

      <div className="relative max-w-[1160px] mx-auto px-5 md:px-10 py-[clamp(72px,12vh,130px)]">
        <div className="text-center mb-[clamp(36px,5vh,56px)]">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-4 py-1.5 font-mono text-[11.5px] tracking-[.2em] uppercase" style={{ color: '#C8FF22' }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#C8FF22', boxShadow: '0 0 10px #C8FF22' }} />
            Canlı panel
          </span>
          <h2 className="mt-6 font-display font-bold text-white leading-[1.05] tracking-[-0.03em] mx-auto max-w-[20ch]" style={{ fontSize: 'clamp(28px,3.6vw,48px)' }}>
            Tarlan tek ekranda, canlı verilerle
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {/* NDVI barlar */}
          <Card icon={leaf} tint="#4ADE80" title="NDVI" sub="Son 10 Gün" badge={<Badge text="İyileşiyor" tint="#4ADE80" up />}>
            <svg viewBox="0 0 300 150" className="w-full">
              {NDVI.map((v, i) => {
                const x = 18 + i * 27.5, w = 18, h = (v / 100) * 120, y = 130 - h;
                const g = h * 0.46, ye = h * 0.31, r = h * 0.23;
                return (
                  <g key={i} className="d-bar" style={{ transformBox: 'fill-box', transformOrigin: 'center bottom' }}>
                    <rect x={x} y={130 - g} width={w} height={g} rx="2" fill="#22C55E" />
                    <rect x={x} y={130 - g - ye} width={w} height={ye} fill="#FACC15" />
                    <rect x={x} y={y} width={w} height={r} rx="2" fill="#EF4444" />
                  </g>
                );
              })}
              <line x1="14" y1="130" x2="292" y2="130" stroke="rgba(255,255,255,.12)" strokeWidth="1" />
            </svg>
          </Card>

          {/* NDVI trend çizgisi */}
          <Card icon={trend} tint="#FB923C" title="NDVI Analizi" sub="Bitki Gelişim Trendi" badge={<Badge text="İyileşiyor" tint="#4ADE80" up />}>
            <svg viewBox="0 0 300 150" className="w-full">
              <defs>
                <linearGradient id="trendg" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#4ADE80" /><stop offset="60%" stopColor="#A3E635" /><stop offset="100%" stopColor="#FACC15" />
                </linearGradient>
              </defs>
              {[26, 62, 98, 134].map((y) => <line key={y} x1="14" y1={y} x2="292" y2={y} stroke="rgba(255,255,255,.06)" strokeWidth="1" />)}
              <path className="d-line" d={TREND} fill="none" stroke="url(#trendg)" strokeWidth="4" strokeLinecap="round"
                style={{ filter: 'drop-shadow(0 0 6px rgba(163,230,53,.5))' }} />
              <circle className="d-dot" cx="14" cy="26" r="5" fill="#4ADE80" />
              <circle className="d-dot" cx="286" cy="140" r="5" fill="#FACC15" />
            </svg>
          </Card>

          {/* Yağış barlar */}
          <Card icon={drop} tint="#38BDF8" title="Yağış Miktarı" sub="Son 12 Ay (mm)" badge={<Badge text="Dengeli" tint="#38BDF8" />}>
            <svg viewBox="0 0 300 150" className="w-full">
              {RAIN.map((v, i) => {
                const x = 16 + i * 23.5, w = 15, h = (v / 200) * 120, y = 130 - h;
                return <rect key={i} className="d-bar" x={x} y={y} width={w} height={h} rx="2.5" fill="#38BDF8"
                  style={{ transformBox: 'fill-box', transformOrigin: 'center bottom' }} />;
              })}
              <line x1="12" y1="130" x2="294" y2="130" stroke="rgba(255,255,255,.12)" strokeWidth="1" />
            </svg>
          </Card>

          {/* Sulama gösterge */}
          <Card icon={drop} tint="#38BDF8" title="Sulama Tüketimi" sub="Bu Ay Toplam">
            <Gauge pct={72} from="#38BDF8" to="#0EA5E9" numRef={num1} label="litre" sub="" />
          </Card>

          {/* Bitki Sağlığı gösterge */}
          <Card icon={leaf} tint="#84CC16" title="Bitki Sağlığı" sub="Genel Durum">
            <Gauge pct={82} from="#FB923C" to="#A3E635" numRef={num2} label="Sağlıklı" sub="%" />
          </Card>

          {/* Verim Tahmini gösterge */}
          <Card icon={tractor} tint="#84CC16" title="Verim Tahmini" sub="Hedefe Oran">
            <Gauge pct={94} from="#FACC15" to="#84CC16" numRef={num3} label="Hedefe Yakın" sub="%" />
          </Card>
        </div>

        <p className="mt-8 text-center text-[13px] text-white/40 max-w-[60ch] mx-auto">
          Görseldeki değerler örnek amaçlıdır; gerçek veriler tarlana ve uydu geçişlerine göre değişir.
        </p>
      </div>
    </section>
  );
}
